import React, { useMemo, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, X } from 'lucide-react';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';

const JobFilters = ({
  searchTerm,
  setSearchTerm,
  locationFilter,
  setLocationFilter,
  employmentTypeFilter,
  setEmploymentTypeFilter,
  sourceFilter,
  setSourceFilter,
  sortBy,
  setSortBy,
  roleFilter,
  setRoleFilter,
  distanceFilter,
  setDistanceFilter,
  onClearFilters,
  jobCount,
  filteredJobCount,
  isCompactMode = false // ADD: Just add this one missing prop with default value
}) => {
  const { theme } = useTheme();
  
  // ADDED: Local search state for debouncing
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  // ADDED: Sync local state with prop changes
  React.useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // FIXED: Debounced search handler - remove the 3+ character requirement that was blocking searches
  const handleSearchChange = useCallback((value) => {
    setLocalSearchTerm(value);
    
    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    // FIXED: Always trigger search after delay, no minimum character requirement
    const newTimer = setTimeout(() => {
      setSearchTerm(value); // FIXED: Always trigger search
    }, value.trim() === '' ? 0 : 300); // Immediate clear, 300ms delay for search (reduced from 500ms)
    
    setSearchDebounceTimer(newTimer);
  }, [setSearchTerm, searchDebounceTimer]);

  // ADDED: Enter key search handler
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Clear any pending debounce timer
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
      // Trigger immediate search
      setSearchTerm(localSearchTerm);
    }
  }, [localSearchTerm, setSearchTerm, searchDebounceTimer]);

  // ADDED: Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  // Add local state to override if parent state isn't working
  const [localDistanceFilter, setLocalDistanceFilter] = React.useState(distanceFilter);
  
  // Sync with prop changes
  React.useEffect(() => {
    setLocalDistanceFilter(distanceFilter);
  }, [distanceFilter]);

  // FIXED: Add effect to reset local distance filter when global filter is cleared
  React.useEffect(() => {
    if (distanceFilter === 0) {
      setLocalDistanceFilter(0);
    }
  }, [distanceFilter]);

  // FIXED: Include role filter and distance filter in active filters check
  const hasActiveFilters = searchTerm || locationFilter || 
                          employmentTypeFilter !== 'all' || 
                          sourceFilter !== 'all' ||
                          roleFilter !== 'all' || 
                          localDistanceFilter !== 0;

  // FIXED: Enhanced distance change handler that ensures parent state is updated
  const handleDistanceChange = (value) => {
    try {
      const numericValue = Number(value);
      
      // Update local state immediately for UI responsiveness
      setLocalDistanceFilter(numericValue);
      
      // FIXED: Always call the parent setter to ensure state sync
      if (typeof setDistanceFilter === 'function') {
        setDistanceFilter(numericValue);
      }
      
      // FIXED: Force immediate re-fetch if needed
      console.log('Distance filter changed to:', numericValue);
      
      // Broadcast the change globally for other components to pick up
      window.currentDistanceFilter = numericValue;
      
      // Dispatch a custom event so other components can listen
      window.dispatchEvent(new CustomEvent('distanceFilterChanged', { 
        detail: { distance: numericValue } 
      }));
      
    } catch (error) {
      console.error('Error setting distance filter:', error);
    }
  };

  // FIXED: Use local state for dropdown value
  const getDistanceValue = () => {
    return String(localDistanceFilter);
  };

  // ADD: Just add this compact mode check at the very beginning
  if (isCompactMode) {
    return (
      <div className={cn(
        "p-3 rounded-lg border",
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-gray-700'
      )}>
        {/* Compact search bar */}
        <div className="mb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
            <Input
              type="text"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                "pl-8 pr-4 py-1 text-sm h-8",
                theme === 'light' 
                  ? 'border-gray-300 bg-white text-gray-900' 
                  : 'border-gray-600 bg-gray-700 text-white'
              )}
            />
          </div>
        </div>

        {/* Compact filter row */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          {/* Role filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className={cn(
              "w-full px-2 py-1 border rounded text-xs h-7",
              theme === 'light' 
                ? 'border-gray-300 bg-white text-gray-900' 
                : 'border-gray-600 bg-gray-700 text-white'
            )}
          >
            <option value="all">All Roles</option>
            <option value="Hygienist/Therapist">Hygienist/Therapist</option>
            <option value="Locum Dentist">Locum Dentist</option>
            <option value="Dental Nurse">Dental Nurse</option>
            <option value="Practice Manager">Practice Manager</option>
            <option value="Receptionist">Receptionist</option>
          </select>

          {/* Distance filter */}
          <select
            value={distanceFilter}
            onChange={(e) => setDistanceFilter(Number(e.target.value))}
            className={cn(
              "w-full px-2 py-1 border rounded text-xs h-7",
              theme === 'light' 
                ? 'border-gray-300 bg-white text-gray-900' 
                : 'border-gray-600 bg-gray-700 text-white'
            )}
          >
            <option value={0}>Any Distance</option>
            <option value={5}>Within 5 miles</option>
            <option value={10}>Within 10 miles</option>
            <option value={25}>Within 25 miles</option>
            <option value={50}>Within 50 miles</option>
          </select>
        </div>

        {/* Results count and clear */}
        <div className="flex items-center justify-between text-xs">
          <span className={cn(
            "text-xs",
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          )}>
            {filteredJobCount} of {jobCount} jobs
          </span>
          
          {(searchTerm || roleFilter !== 'all' || distanceFilter > 0) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className={cn(
      "mb-4 relative z-[50]", // Reduced from mb-6 to mb-4
      theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
    )}>
      <CardContent className="p-2"> {/* Reduced from p-3 to p-2 */}
        <div className="space-y-2"> {/* Reduced from space-y-3 to space-y-2 */}
          
          {/* Desktop Layout - Single Row with Search + Filters */}
          <div className="hidden md:flex items-end gap-2"> {/* Reduced gap from gap-3 to gap-2 */}
            {/* Search - ENHANCED: Added debouncing and enter-key search */}
            <div className="flex-1 min-w-0 max-w-md"> {/* FIXED: Added max-w-md to limit search width */}
              <Label className={cn(
                "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              )}>
                Search
              </Label>
              <div className="relative">
                <Search className={cn(
                  "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 z-10",
                  theme === 'light' ? 'text-gray-400' : 'text-gray-500'
                )} />
                <Input
                  placeholder="Jobs, locations, practices... (Press Enter to search)"
                  value={localSearchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className={cn(
                    "pl-10 h-8",
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black focus:bg-white hover:bg-white'
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}
                  style={{
                    ...(theme === 'light' && {
                      backgroundColor: '#ffffff',
                      borderColor: '#d1d5db'
                    })
                  }}
                />
                {/* FIXED: Updated search indicator */}
                {localSearchTerm !== searchTerm && localSearchTerm.length > 0 && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                    Press Enter
                  </div>
                )}
              </div>
            </div>

            {/* Filter Controls - Compact */}
            <div className="flex items-end gap-1.5"> {/* Reduced gap from gap-2 to gap-1.5 */}
              {/* Role Filter - FIXED: Increased width for desktop */}
              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block",
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Role
                </Label>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className={cn(
                    "w-36 h-8 text-xs", // FIXED: Increased from w-24 to w-36 for "Hygienist/Therapist"
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="Hygienist/Therapist">Hygienist/Therapist</SelectItem>
                    <SelectItem value="Locum Dentist">Locum Dentist</SelectItem>
                    <SelectItem value="Dental Nurse">Dental Nurse</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Employment Type */}
              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Type
                </Label>
                <Select value={employmentTypeFilter} onValueChange={setEmploymentTypeFilter}>
                  <SelectTrigger className={cn(
                    "w-20 h-8 text-xs", // Reduced from w-24 h-9 to w-20 h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="NHS">NHS</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Locum">Locum</SelectItem>
                    <SelectItem value="Permanent">Permanent</SelectItem>
                    <SelectItem value="Temporary">Temporary</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Distance */}
              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Distance
                </Label>
                <Select value={getDistanceValue()} onValueChange={handleDistanceChange}>
                  <SelectTrigger className={cn(
                    "w-16 h-8 text-xs", // Reduced from w-20 h-9 to w-16 h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="0">Any</SelectItem>
                    <SelectItem value="5">5mi</SelectItem>
                    <SelectItem value="10">10mi</SelectItem>
                    <SelectItem value="15">15mi</SelectItem>
                    <SelectItem value="20">20mi</SelectItem>
                    <SelectItem value="30">30mi</SelectItem>
                    <SelectItem value="50">50mi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Source */}
              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Source
                </Label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className={cn(
                    "w-16 h-8 text-xs", // Reduced from w-20 h-9 to w-16 h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="practice">Practice</SelectItem>
                    <SelectItem value="rss">BDJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sort */}
              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Sort
                </Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className={cn(
                    "w-20 h-8 text-xs", // Reduced from w-24 h-9 to w-20 h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="distance">Distance</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="oldest">Oldest</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                disabled={!hasActiveFilters}
                className={cn(
                  "h-8 px-2", // Reduced from h-9 px-3 to h-8 px-2
                  theme === 'light' 
                    ? 'text-gray-700 border-gray-300 hover:bg-gray-100' 
                    : 'text-gray-300 border-gray-600 hover:bg-gray-700'
                )}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Mobile Layout - Stacked for smaller screens */}
          <div className="md:hidden space-y-2"> {/* Reduced from space-y-3 to space-y-2 */}
            {/* Search Row - ENHANCED */}
            <div>
              <Label className={cn(
                "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              )}>
                Search
              </Label>
              <div className="relative">
                <Search className={cn(
                  "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 z-10",
                  theme === 'light' ? 'text-gray-400' : 'text-gray-500'
                )} />
                <Input
                  placeholder="Jobs, locations, practices..."
                  value={localSearchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className={cn(
                    "pl-10 h-8 w-full", // Reduced from h-9 to h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black focus:bg-white hover:bg-white'
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}
                  style={{
                    ...(theme === 'light' && {
                      backgroundColor: '#ffffff',
                      borderColor: '#d1d5db'
                    })
                  }}
                />
              </div>
            </div>

            {/* Filters Row - Grid for mobile */}
            <div className="grid grid-cols-2 gap-1.5"> {/* Reduced gap from gap-2 to gap-1.5 */}
              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Role
                </Label>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className={cn(
                    "w-full h-8 text-xs", // Reduced from h-9 to h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="Hygienist/Therapist">Hygienist/Therapist</SelectItem>
                    <SelectItem value="Locum Dentist">Locum Dentist</SelectItem>
                    <SelectItem value="Dental Nurse">Dental Nurse</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Type
                </Label>
                <Select value={employmentTypeFilter} onValueChange={setEmploymentTypeFilter}>
                  <SelectTrigger className={cn(
                    "w-full h-8 text-xs", // Reduced from h-9 to h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="NHS">NHS</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Locum">Locum</SelectItem>
                    <SelectItem value="Permanent">Permanent</SelectItem>
                    <SelectItem value="Temporary">Temporary</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Distance
                </Label>
                <Select value={getDistanceValue()} onValueChange={handleDistanceChange}>
                  <SelectTrigger className={cn(
                    "w-full h-8 text-xs", // Reduced from h-9 to h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="0">Any</SelectItem>
                    <SelectItem value="5">5mi</SelectItem>
                    <SelectItem value="10">10mi</SelectItem>
                    <SelectItem value="15">15mi</SelectItem>
                    <SelectItem value="20">20mi</SelectItem>
                    <SelectItem value="30">30mi</SelectItem>
                    <SelectItem value="50">50mi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Source
                </Label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className={cn(
                    "w-full h-8 text-xs", // Reduced from h-9 to h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="practice">Practice</SelectItem>
                    <SelectItem value="rss">BDJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className={cn(
                  "text-xs font-medium mb-0.5 block", // Reduced from mb-1 to mb-0.5
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                )}>
                  Sort
                </Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className={cn(
                    "w-full h-8 text-xs", // Reduced from w-9 to h-8
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-gray-700 text-white'
                  )}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="distance">Distance</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="oldest">Oldest</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Clear Button - Mobile */}
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              disabled={!hasActiveFilters}
              className={cn(
                "w-full h-8 flex items-center justify-center gap-2", // Reduced from h-9 to h-8
                theme === 'light' 
                  ? 'text-gray-700 border-gray-300 hover:bg-gray-100' 
                  : 'text-gray-300 border-gray-600 hover:bg-gray-700'
              )}
            >
              <X className="h-3 w-3" />
              <span className="text-xs">Clear Filters</span>
            </Button>
          </div>

          {/* Job Count Display - Compact */}
          <div className={cn(
            "text-xs flex justify-between items-center pt-1", // Added pt-1 for tiny top padding
            theme === 'light' ? 'text-gray-500' : 'text-gray-400'
          )}>
            <span>
              {filteredJobCount !== undefined ? (
                `${filteredJobCount} of ${jobCount || 0} jobs`
              ) : (
                `${jobCount || 0} jobs found`
              )}
            </span>
            {hasActiveFilters && (
              <span className="text-purple-600 dark:text-purple-400 text-xs">
                Filters active
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default JobFilters;
